import { PawPrint, Plus, Trash2 } from "lucide-react";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2.5 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";
const LBL = "text-xs font-medium text-charcoal-500 mb-1 block";

export const emptyPet = () => ({
  name: "", species: "dog", breed: "", sex: "", size: "", weight: "", age: "",
  spayed_neutered: false, energy_level: "", crate_trained: false,
  medical_needs: "", vaccination_status: "", favorite_treat: "", temperament: "", photo: "",
});

function PetForm({ pet, idx, onChange, onRemove, compact }) {
  const set = (k, v) => onChange({ ...pet, [k]: v });
  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      const scale = Math.min(1, 320 / Math.max(img.width, img.height));
      c.width = img.width * scale; c.height = img.height * scale;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      set("photo", c.toDataURL("image/jpeg", 0.8));
    };
    img.src = URL.createObjectURL(file);
  };
  return (
    <div data-testid={`pet-form-${idx}`} className="rounded-xl border border-sand-200 bg-sand-100/60 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="inline-flex items-center gap-1.5 text-sm font-medium text-terracotta"><PawPrint size={14} /> Pet {idx + 1}</p>
        <button type="button" data-testid={`pet-remove-${idx}`} onClick={onRemove} className="p-1 text-charcoal-400 hover:text-red-600"><Trash2 size={14} /></button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div><label className={LBL}>Name</label><input data-testid={`pet-name-${idx}`} className={FIELD} value={pet.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div><label className={LBL}>Species</label>
          <select data-testid={`pet-species-${idx}`} className={FIELD} value={pet.species} onChange={(e) => set("species", e.target.value)}>
            <option value="dog">Dog</option><option value="cat">Cat</option><option value="other">Other</option>
          </select></div>
        <div><label className={LBL}>Breed</label><input className={FIELD} value={pet.breed} onChange={(e) => set("breed", e.target.value)} /></div>
      </div>
      {!compact && (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <div><label className={LBL}>Sex</label>
              <select className={FIELD} value={pet.sex} onChange={(e) => set("sex", e.target.value)}>
                <option value="">—</option><option value="female">Female</option><option value="male">Male</option>
              </select></div>
            <div><label className={LBL}>Size</label>
              <select data-testid={`pet-size-${idx}`} className={FIELD} value={pet.size} onChange={(e) => set("size", e.target.value)}>
                <option value="">—</option><option value="small">Small</option><option value="medium">Medium</option><option value="large">Large</option>
              </select></div>
            <div><label className={LBL}>Weight</label><input className={FIELD} placeholder="e.g. 40 lb" value={pet.weight} onChange={(e) => set("weight", e.target.value)} /></div>
            <div><label className={LBL}>Age</label><input className={FIELD} placeholder="e.g. 4 years" value={pet.age} onChange={(e) => set("age", e.target.value)} /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><label className={LBL}>Energy level</label>
              <select className={FIELD} value={pet.energy_level} onChange={(e) => set("energy_level", e.target.value)}>
                <option value="">—</option><option value="calm">Calm</option><option value="moderate">Moderate</option><option value="energetic">Energetic</option>
              </select></div>
            <div><label className={LBL}>Vaccination status</label><input className={FIELD} placeholder="e.g. up to date, rabies 2025" value={pet.vaccination_status} onChange={(e) => set("vaccination_status", e.target.value)} /></div>
            <div><label className={LBL}>Favorite toy or treat</label><input className={FIELD} value={pet.favorite_treat} onChange={(e) => set("favorite_treat", e.target.value)} /></div>
          </div>
          <div className="flex flex-wrap gap-5">
            <label className="inline-flex items-center gap-2 text-sm text-charcoal-700">
              <input type="checkbox" checked={pet.spayed_neutered} onChange={(e) => set("spayed_neutered", e.target.checked)} className="accent-terracotta" /> Spayed / neutered
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-charcoal-700">
              <input type="checkbox" checked={pet.crate_trained} onChange={(e) => set("crate_trained", e.target.checked)} className="accent-terracotta" /> Crate-trained
            </label>
          </div>
          <div><label className={LBL}>Medical needs / medications</label><input className={FIELD} value={pet.medical_needs} onChange={(e) => set("medical_needs", e.target.value)} /></div>
          <div><label className={LBL}>Temperament (anything we should know)</label>
            <textarea data-testid={`pet-temperament-${idx}`} rows={2} className={FIELD} placeholder="e.g. anxious in cars, reactive to other dogs" value={pet.temperament} onChange={(e) => set("temperament", e.target.value)} /></div>
          <div className="flex items-center gap-3">
            {pet.photo && <img src={pet.photo} alt={pet.name || "pet"} className="h-12 w-12 rounded-full object-cover border border-sand-300" />}
            <label className="cursor-pointer text-xs font-medium text-terracotta hover:underline">
              {pet.photo ? "Change photo" : "Add a photo (optional)"}
              <input type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
            </label>
          </div>
        </>
      )}
    </div>
  );
}

export const FamilyPetsFields = ({ value, onChange, compact = false, showPersonal = true }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const pets = value.pets || [];
  const setPet = (i, p) => set("pets", pets.map((x, j) => (j === i ? p : x)));
  return (
    <div className="space-y-4" data-testid="family-pets-fields">
      {showPersonal && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><label className={LBL}>Preferred name / nickname</label>
              <input data-testid="profile-preferred-name" className={FIELD} placeholder="What should we call you?" value={value.preferred_name || ""} onChange={(e) => set("preferred_name", e.target.value)} /></div>
            <div><label className={LBL}>Adults traveling</label>
              <input data-testid="profile-adults" type="number" min="0" max="20" className={FIELD} value={value.party_adults ?? ""} onChange={(e) => set("party_adults", e.target.value === "" ? null : Number(e.target.value))} /></div>
            <div><label className={LBL}>Kids traveling</label>
              <input data-testid="profile-kids" type="number" min="0" max="20" className={FIELD} value={value.party_kids ?? ""} onChange={(e) => set("party_kids", e.target.value === "" ? null : Number(e.target.value))} /></div>
          </div>
          <div><label className={LBL}>Anything that would make your move easier or more comfortable for you?</label>
            <textarea data-testid="profile-notes" rows={2} className={FIELD} value={value.personalization_notes || ""} onChange={(e) => set("personalization_notes", e.target.value)} /></div>
        </>
      )}
      <div>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-charcoal-700">
          <input data-testid="pets-toggle" type="checkbox" className="accent-terracotta"
            checked={pets.length > 0}
            onChange={(e) => set("pets", e.target.checked ? [emptyPet()] : [])} />
          Traveling with pets? <PawPrint size={14} className="text-terracotta" />
        </label>
      </div>
      {pets.length > 0 && (
        <div className="space-y-3">
          {pets.map((p, i) => (
            <PetForm key={p.id || i} pet={p} idx={i} compact={compact}
              onChange={(np) => setPet(i, np)}
              onRemove={() => set("pets", pets.filter((_, j) => j !== i))} />
          ))}
          <button type="button" data-testid="pet-add-btn" onClick={() => set("pets", [...pets, emptyPet()])}
            className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 px-4 py-2 text-xs font-medium text-charcoal-700 hover:border-terracotta hover:text-terracotta">
            <Plus size={13} /> Add another pet
          </button>
        </div>
      )}
      <p className="text-xs text-charcoal-400">Every field here is optional — share as much or as little as you like.</p>
    </div>
  );
};
